import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import {
  createStudentEvaluation,
  getEvaluationByStudentId,
  updateStudentEvaluation,
} from "../../services/StudentServices";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import printerIcon from "../../assets/printer_purple.png";
import SectionDivider from "../../components/SectionDivider";
import { RedFlagIndicator } from "../../components/RedFlagIndicator";
import CustomCheckbox from "../../components/CustomCheckbox";
import StudentEvaluationTemplate from "./StudentEvaluationTemplate";
import { useReactToPrint } from "react-to-print";

const StudentEvaluationModal = ({
  isOpen,
  onClose,
  onEvaluationUpdated,
  studentId,
  studentInfo,
}) => {
  const { t } = useTranslation("general");
  const evaluationTemplateRef = useRef();

  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingEvaluation, setExistingEvaluation] = useState(null);

  // Evaluation grades options
  const evaluationOptions = [
    { title: t("excellent"), value: "excellent" },
    { title: t("veryGood"), value: "veryGood" },
    { title: t("good"), value: "good" },
    { title: t("medium"), value: "medium" },
    { title: t("poor"), value: "poor" },
    { title: t("redoTest"), value: "redoTest" },
  ];

  const [evaluationFields, setEvaluationFields] = useState({
    subjectEvaluation: {
      Math: "",
      English: "",
      Arabic: "",
    },
    behavior: "",
    specialCases: "",
    speaking: "",
    attentionAndFocus: "",
    notes: "",
    redFlag: false,
  });

  const fetchStudentEvaluation = async () => {
    try {
      setLoadingModal(true);
      const response = await getEvaluationByStudentId(studentId);

      if (response.status === 200 && response.data.length > 0) {
        const evaluation = response.data[0]; // Get the first evaluation
        evaluation.student = {...evaluation.student, schoolClass:{ ...studentInfo.schoolClass}}
        setExistingEvaluation(evaluation);

        // Set the form fields with existing data
        setEvaluationFields({
          subjectEvaluation: evaluation.subjectEvaluation || {
            Math: "",
            English: "",
            Arabic: "",
          },
          behavior: evaluation.behavior || "",
          specialCases: evaluation.specialCases || "",
          speaking: evaluation.speaking || "",
          attentionAndFocus: evaluation.attentionAndFocus || "",
          notes: evaluation.notes || "",
          redFlag: evaluation.redFlag || false,
        });
      } else {
        // No evaluation exists, reset to empty state
        setExistingEvaluation(null);
        resetForm();
      }
    } catch (error) {
      console.error("Error fetching evaluation:", error);
      setExistingEvaluation(null);
      resetForm();
    } finally {
      setLoadingModal(false);
    }
  };

  const resetForm = () => {
    setEvaluationFields({
      subjectEvaluation: {
        Math: "",
        English: "",
        Arabic: "",
      },
      behavior: "",
      specialCases: "",
      speaking: "",
      attentionAndFocus: "",
      notes: "",
      redFlag: false,
    });
  };

  const handleInputChange = (e, fieldName, subjectName = null) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setEvaluationFields((prevFields) => {
      if (fieldName === "subjectEvaluation" && subjectName) {
        return {
          ...prevFields,
          subjectEvaluation: {
            ...prevFields.subjectEvaluation,
            [subjectName]: value,
          },
        };
      }

      return {
        ...prevFields,
        [fieldName]: value,
      };
    });
  };

  const handleSaveEvaluation = async (e) => {
    e.preventDefault();

    try {
      setLoadingModal(true);

      const evaluationData = {
        studentId: studentId,
        subjectEvaluation: evaluationFields.subjectEvaluation,
        behavior: evaluationFields.behavior,
        specialCases: evaluationFields.specialCases,
        speaking: evaluationFields.speaking,
        attentionAndFocus: evaluationFields.attentionAndFocus,
        notes: evaluationFields.notes,
        redFlag: evaluationFields.redFlag,
      };

      let response;
      if (existingEvaluation) {
        // Update existing evaluation
        response = await updateStudentEvaluation(
          existingEvaluation.id,
          evaluationData
        );
      } else {
        // Create new evaluation
        response = await createStudentEvaluation(evaluationData);
      }

      if (response.status === 200 || response.status === 201) {
        setError(false);
        setErrorMessage("");
        setIsEditMode(false);

        // Refresh the evaluation data
        await fetchStudentEvaluation();

        // Call parent callback to refresh data
        if (onEvaluationUpdated) {
          onEvaluationUpdated();
        }
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        setError(true);
        setErrorMessage(error.response.data.error);
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage("An unexpected error occurred");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancel = () => {
    setError(false);
    setErrorMessage("");
    setIsEditMode(false);

    // Reset form to original state
    if (existingEvaluation) {
      setEvaluationFields({
        subjectEvaluation: existingEvaluation.subjectEvaluation || {
          Math: "",
          English: "",
          Arabic: "",
        },
        behavior: existingEvaluation.behavior || "",
        specialCases: existingEvaluation.specialCases || "",
        speaking: existingEvaluation.speaking || "",
        attentionAndFocus: existingEvaluation.attentionAndFocus || "",
        notes: existingEvaluation.notes || "",
        redFlag: existingEvaluation.redFlag || false,
      });
    } else {
      resetForm();
    }

    onClose();
  };

  const handlePrint = useReactToPrint({
    contentRef: evaluationTemplateRef,
    documentTitle: `${t("studentEvaluation")}-${studentInfo?.fullName}-(${
      studentInfo?.studentCode
    })-${new Date().toLocaleDateString()}`,
    pageStyle: `
    @page {
      margin: 0 !important;
      size: A4;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: 100% !important;
      }
      body {
        -webkit-print-color-adjust: exact;
        margin: 0 !important;
        padding: 0 !important;
        display: block !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `,
  });

  const handleEdit = () => {
    setIsEditMode(true);
    setError(false);
    setErrorMessage("");
  };

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchStudentEvaluation();
      setIsEditMode(false);
    }
  }, [isOpen, studentId]);

  if (!isOpen) return null;

  return (
    <Modal
      title={t("studentEvaluation")}
      onClose={handleCancel}
      footer={
        <>
          {loadingModal ? <Loader /> : null}
          <section className="edit-user-actions">
            {isEditMode ? (
              <CustomButton
                title={t("save")}
                onClick={handleSaveEvaluation}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
            ) : (
              <>
                <CustomButton
                  title={existingEvaluation ? t("edit") : t("createEvaluation")}
                  variant="blank"
                  onClick={handleEdit}
                  icon={editIcon}
                  iconSize={16}
                  disabled={loadingModal}
                />
                <CustomButton
                  title={t("printEvaluation")}
                  onClick={handlePrint}
                  variant="blank"
                  icon={printerIcon}
                  iconSize={20}
                  disabled={loadingModal || !existingEvaluation}
                />
              </>
            )}
            {isEditMode && (
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
                icon={closeIcon}
                iconSize={14}
              />
            )}
          </section>
        </>
      }
    >
      <form onSubmit={handleSaveEvaluation}>
        <div>
          {error && (
            <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
              <ErrorBox>{errorMessage}</ErrorBox>
            </div>
          )}

          <div className="edit-user-entity">
            {/* Student Info */}
            <div className="o-row">
              <span className="u-width--min-width_130">
                {t("studentName")}:{" "}
              </span>
              <span className="t-bold_bold">{studentInfo.fullName}</span>
            </div>
            <div className="o-row u-margin--bottom_300">
              <span className="u-width--min-width_130">{t("studentId")}: </span>
              <span className="t-bold_bold">{studentInfo.studentCode}</span>
            </div>
            {/* Subject Evaluations */}
            <SectionDivider title={"level"} />
            {/* Math */}
            <div className="o-row o-row--vertical-align_center u-margin--bottom_200">
              <span className="u-width--min-width_130">{t("math")}: </span>
              {isEditMode ? (
                <Dropdown
                  options={evaluationOptions}
                  selected={{
                    title:
                      evaluationFields.subjectEvaluation.Math ||
                      t("notEvaluated"),
                    value: evaluationFields.subjectEvaluation.Math,
                  }}
                  setSelected={(selected) => {
                    handleInputChange(
                      { target: { value: selected.value } },
                      "subjectEvaluation",
                      "Math"
                    );
                  }}
                  thick
                  flex
                />
              ) : (
                <span className="t-bold_bold">
                  {t(evaluationFields.subjectEvaluation.Math) ||
                    t("notEvaluated")}
                </span>
              )}
            </div>
            {/* English */}
            <div className="o-row o-row--vertical-align_center u-margin--bottom_200 ">
              <span className="u-width--min-width_130">{t("english")}: </span>
              {isEditMode ? (
                <Dropdown
                  options={evaluationOptions}
                  selected={{
                    title:
                      evaluationFields.subjectEvaluation.English ||
                      t("notEvaluated"),
                    value: evaluationFields.subjectEvaluation.English,
                  }}
                  setSelected={(selected) => {
                    handleInputChange(
                      { target: { value: selected.value } },
                      "subjectEvaluation",
                      "English"
                    );
                  }}
                  thick
                  flex
                />
              ) : (
                <span className="t-bold_bold">
                  {t(evaluationFields.subjectEvaluation.English) ||
                    t("notEvaluated")}
                </span>
              )}
            </div>
            {/* Arabic */}
            <div className="o-row o-row--vertical-align_center">
              <span className="u-width--min-width_130">{t("arabic")}: </span>
              {isEditMode ? (
                <Dropdown
                  options={evaluationOptions}
                  selected={{
                    title:
                      evaluationFields.subjectEvaluation.Arabic ||
                      t("notEvaluated"),
                    value: evaluationFields.subjectEvaluation.Arabic,
                  }}
                  setSelected={(selected) => {
                    handleInputChange(
                      { target: { value: selected.value } },
                      "subjectEvaluation",
                      "Arabic"
                    );
                  }}
                  thick
                  flex
                />
              ) : (
                <span className="t-bold_bold">
                  {t(evaluationFields.subjectEvaluation.Arabic) ||
                    t("notEvaluated")}
                </span>
              )}
            </div>

            {/* Other Fields */}
            <div className="o-column o-column--gap-size_300">
              <SectionDivider title={"generalBehavior"} />
              <div className="o-row u-margin--bottom_200">
                <span className="u-width--min-width_130">
                  {t("behavior")}:{" "}
                </span>
                {isEditMode ? (
                  <CustomInput
                    name="behavior"
                    type="textarea"
                    value={evaluationFields.behavior}
                    onChange={(e) => handleInputChange(e, "behavior")}
                    flex
                  />
                ) : (
                  <span className="t-bold_bold u-width--max-width_300 t-break-word">
                    {evaluationFields.behavior || t("notEvaluated")}
                  </span>
                )}
              </div>

              <div className="o-row u-margin--bottom_200">
                <span className="u-width--min-width_130">
                  {t("specialCases")}:{" "}
                </span>
                {isEditMode ? (
                  <CustomInput
                    name="specialCases"
                    type="textarea"
                    value={evaluationFields.specialCases}
                    onChange={(e) => handleInputChange(e, "specialCases")}
                    flex
                    thick
                  />
                ) : (
                  <span className="t-bold_bold u-width--max-width_300 t-break-word">
                    {evaluationFields.specialCases || t("notEvaluated")}
                  </span>
                )}
              </div>

              <div className="o-row u-margin--bottom_200">
                <span className="u-width--min-width_130">
                  {t("speaking")}:{" "}
                </span>
                {isEditMode ? (
                  <CustomInput
                    name="speaking"
                    type="textarea"
                    value={evaluationFields.speaking}
                    onChange={(e) => handleInputChange(e, "speaking")}
                    flex
                    thick
                  />
                ) : (
                  <span className="t-bold_bold u-width--max-width_300 t-break-word">
                    {evaluationFields.speaking || t("notEvaluated")}
                  </span>
                )}
              </div>

              <div className="o-row">
                <span className="u-width--min-width_130">
                  {t("attentionAndFocus")}:{" "}
                </span>
                {isEditMode ? (
                  <CustomInput
                    name="attentionAndFocus"
                    type="textarea"
                    value={evaluationFields.attentionAndFocus}
                    onChange={(e) => handleInputChange(e, "attentionAndFocus")}
                    flex
                    thick
                  />
                ) : (
                  <span className="t-bold_bold u-width--max-width_300 t-break-word">
                    {evaluationFields.attentionAndFocus || t("notEvaluated")}
                  </span>
                )}
              </div>

              <SectionDivider title={"notes"} />
              <div className="o-row  u-margin--bottom_200">
                <span className="u-width--min-width_130">{t("notes")}: </span>
                {isEditMode ? (
                  <CustomInput
                    name="notes"
                    type="textarea"
                    value={evaluationFields.notes}
                    onChange={(e) => handleInputChange(e, "notes")}
                    flex
                  />
                ) : (
                  <span className="t-bold_bold u-width--max-width_300 t-break-word">
                    {evaluationFields.notes || t("notEvaluated")}
                  </span>
                )}
              </div>

              {/* Red Flag */}
              <div className="o-row u-margin--bottom_200">
                {isEditMode ? (
                  <div className="o-row o-row--vertical-align_center u-width_full-width">
                    <span className="u-width--min-width_130">
                      {t("redFlag")}:
                    </span>
                    <CustomCheckbox
                      onClick={(e) => handleInputChange(e, "redFlag")}
                      checked={evaluationFields.redFlag}
                    />
                  </div>
                ) : (
                  <div className="o-row o-row--gap-size_200 o-row--vertical-align_center u-width_full-width">
                    <span className="u-width--min-width_130">
                      {t("redFlag")}:
                    </span>
                    <RedFlagIndicator isRedFlag={evaluationFields.redFlag} />
                    <span
                      className="u-width--min-width_50 t-text-align_center"
                      style={{
                        color: evaluationFields.redFlag ? "#ff4444" : "green",
                        fontWeight: evaluationFields.redFlag
                          ? "bold"
                          : "normal",
                      }}
                    >
                      {evaluationFields.redFlag ? t("yes") : t("no")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      <StudentEvaluationTemplate
        ref={evaluationTemplateRef}
        evaluationData={existingEvaluation}
      />
    </Modal>
  );
};

export default StudentEvaluationModal;
